import { DEFAULT_I18N_LANGUAGE } from './constants';
import { getTranslationValue } from './getTranslationValue';
import { interpolateTranslation } from './interpolateTranslation';
import { platformI18nResources } from './resources';
import type {
  I18nKey,
  I18nLanguage,
  I18nNamespace,
  I18nScopedKey,
  I18nTranslateFunction,
  I18nTranslateOptions,
} from './types';

type RuntimeTranslate = I18nTranslateFunction<I18nKey>;

type RuntimeI18nState = {
  language: I18nLanguage;
  t: RuntimeTranslate;
};

type ScopedTranslateOptions<Namespace extends I18nNamespace> = I18nTranslateOptions & {
  namespace: Namespace;
};

type NamespacedTranslateArgs = {
  [Namespace in I18nNamespace]: [key: I18nScopedKey<Namespace>, options: ScopedTranslateOptions<Namespace>];
}[I18nNamespace];

type TranslateOptions = I18nTranslateOptions & {
  namespace?: I18nNamespace;
};

const defaultTranslate: RuntimeTranslate = (key, options) => {
  const translatedValue = getTranslationValue(platformI18nResources[DEFAULT_I18N_LANGUAGE], key);
  const rawValue = translatedValue ?? options?.defaultValue ?? key;

  return interpolateTranslation(rawValue, options);
};

const defaultRuntimeI18nState: RuntimeI18nState = {
  language: DEFAULT_I18N_LANGUAGE,
  t: defaultTranslate,
};

let runtimeI18nState = defaultRuntimeI18nState;

export const setRuntimeI18nState = (state: RuntimeI18nState) => {
  runtimeI18nState = state;
};

export const getRuntimeI18nState = () => runtimeI18nState;
export const clearRuntimeI18nState = () => {
  runtimeI18nState = defaultRuntimeI18nState;
};

export function translate(...args: NamespacedTranslateArgs): string;
export function translate(key: I18nKey, options?: I18nTranslateOptions): string;
export function translate(...args: [key: string, options?: TranslateOptions]): string {
  const [key, options] = args;
  const namespace = options?.namespace;
  const translateOptions: I18nTranslateOptions | undefined = options
    ? {
        defaultValue: options.defaultValue,
        values: options.values,
      }
    : undefined;
  const namespacedKey = namespace ? `${namespace}.${key}` : key;

  return runtimeI18nState.t(namespacedKey as I18nKey, translateOptions);
}
